import { Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { displaySessionExpirePage } from '@capital-access/common/actions';

@Injectable({
  providedIn: 'root'
})
export class SessionTrackingService {
  private activityTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly MILLISECONDS_IN_MINUTE = 60000;
  private readonly defaultTimeout = 480; // default to 8 hours
  private eventListeners: Array<{ event: string; handler: () => void }> = [];
  private timeoutDuration: number = 0;
  private lastActivityTime: number = Date.now();
  private isBrowserTabVisible: boolean = true;
  private visibilityChangeHandler?: () => void;

  constructor(private ngZone: NgZone, private store: Store) {}

  startTracking(timeoutDuration: number) {
    if (timeoutDuration < 0) return;

    if (timeoutDuration === 0) timeoutDuration = this.defaultTimeout;

    this.timeoutDuration = timeoutDuration;
    this.lastActivityTime = Date.now();

    // Clean up existing listeners first
    this.stopTracking();
    this.setupBrowserTabVisibilityListener();

    this.resetTimeout(timeoutDuration);

    ['mousemove', 'keydown', 'click', 'touchend'].forEach(event => {
      const handler = () => this.onUserActivity();
      window.addEventListener(event, handler);
      this.eventListeners.push({ event, handler });
    });
  }

  stopTracking() {
    // Clear timeout
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      this.activityTimeout = null;
    }

    // Remove all event listeners
    this.eventListeners.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler);
    });
    this.eventListeners = [];

    // Remove browser tab visibility change listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = undefined;
    }
  }

  private setupBrowserTabVisibilityListener() {
    this.visibilityChangeHandler = () => {
      this.isBrowserTabVisible = !document.hidden;

      if (this.isBrowserTabVisible) {
        const timeElapsed = Date.now() - this.lastActivityTime;
        const timeoutMs = this.timeoutDuration * this.MILLISECONDS_IN_MINUTE;

        if (timeElapsed >= timeoutMs) {
          this.handleSessionTimeoutForThisBrowserTab();
        } else {
          const remainingTime = Math.ceil((timeoutMs - timeElapsed) / this.MILLISECONDS_IN_MINUTE);
          this.resetTimeout(remainingTime);
        }
      } else {
        if (this.activityTimeout) {
          clearTimeout(this.activityTimeout);
          this.activityTimeout = null;
        }
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  private onUserActivity() {
    this.lastActivityTime = Date.now();
    this.resetTimeout(this.timeoutDuration);
  }

  private resetTimeout(timeoutDuration: number) {
    if (this.activityTimeout) clearTimeout(this.activityTimeout);

    // Only set timeout if browser tab is visible
    if (!this.isBrowserTabVisible) return;

    this.activityTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        this.handleSessionTimeoutForThisBrowserTab();
      });
    }, timeoutDuration * this.MILLISECONDS_IN_MINUTE);
  }

  private handleSessionTimeoutForThisBrowserTab() {
    this.stopTracking();
    this.store.dispatch(displaySessionExpirePage());
  }
}
